import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { postJson } from "../api";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Field, FormActions, Notice, PageHead, TextField, showResult, useToast } from "../ui/kit";
import { PageState } from "../ui/pieces";
import { errorText, type AiError } from "./ai";
import { protocolName, type AiOptions } from "./CustomProviderPage";
import { useAiData, useTitle } from "./parts";
import { RowsSkeleton } from "./widgets";
import styles from "./ai.module.css";

/** Nombre de cada capacidad del catálogo que se puede marcar en un modelo manual. */
const capabilityKeys: Record<string, string> = {
  TEXT: "MODELS_CAP_TEXT",
  IMAGE_INPUT: "MODELS_CAP_IMAGES",
  TOOL_CALLING: "MODELS_CAP_TOOLS",
  EMBEDDING: "MODELS_CAP_EMBEDDING",
  STRUCTURED_OUTPUT: "NPA_AI_CAP_STRUCTURED",
  IMAGE_GENERATION: "NPA_AI_CAP_IMAGE_GENERATION",
  AUDIO_INPUT: "NPA_AI_CAP_AUDIO_INPUT",
  AUDIO_GENERATION: "NPA_AI_CAP_AUDIO_GENERATION",
  VIDEO_INPUT: "NPA_AI_CAP_VIDEO_INPUT",
  VIDEO_GENERATION: "NPA_AI_CAP_VIDEO_GENERATION",
};
/** Las del uso diario primero (Texto · Imágenes · Herramientas · Embedding), después las demás del catálogo. */
const capabilityOrder = Object.keys(capabilityKeys);

type FieldError = { field: string; messageKey: string };

/**
 * IA › Modelos › ← Modelo manual (§5.9 `ia-modelo-manual`): proveedor, protocolo, nombre, identificador, capacidades y
 * precios opcionales. Valida antes de enviar (al menos una capacidad, precios ≥ 0, proveedor existente): el error va en
 * cada campo y un resumen arriba; se borra en cuanto el valor es válido (E81). Al crear vuelve a Modelos con un toast.
 */
export function ManualModelPage() {
  const { L, locale } = useTexts();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data, state, retry } = useAiData<AiOptions>("/api/admin/ai/options");
  const [chosenProvider, setProviderId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [chosenProtocol, setProtocol] = useState("OPENAI_CHAT");
  const [capabilities, setCapabilities] = useState<string[]>(["TEXT"]);
  const [inputPrice, setInputPrice] = useState("");
  const [cachedPrice, setCachedPrice] = useState("");
  const [outputPrice, setOutputPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [summary, setSummary] = useState(false);
  useTitle(L("ADM_AI_MANUAL_TITLE"));
  const back = { href: "/Ai/Modelos/Index", label: L("ADM_AI_MODELS") };
  const head = <PageHead title={L("ADM_AI_MANUAL_TITLE")} sub={L("ADM_AI_MANUAL_SUB")} back={back} />;
  if (state === "failed" || state === "missing")
    return (
      <PageState
        head={head}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );
  if (!data)
    return (
      <>
        {head}
        <RowsSkeleton rows={3} />
      </>
    );

  // El proveedor de ?providerId= (o el primero) mientras la persona no elija otro.
  const wanted = params.get("providerId");
  const providerId =
    chosenProvider ??
    (data.providers.some((item) => String(item.providerId) === wanted)
      ? wanted!
      : String(data.providers[0]?.providerId ?? ""));
  const protocol = data.protocols.includes(chosenProtocol) ? chosenProtocol : (data.protocols[0] ?? chosenProtocol);
  const parse = (value: string) => (value.trim() === "" ? null : Number(value.replace(",", ".")));
  const priceError = (value: string) => {
    const number = parse(value);
    return number !== null && (Number.isNaN(number) || number < 0) ? L("AI_PRICE_INVALID") : null;
  };
  /** Errores de lo escrito ahora; la misma regla al guardar y al corregir un campo. */
  const check = (values: {
    name: string;
    code: string;
    capabilities: string[];
    input: string;
    cached: string;
    output: string;
  }) => {
    const local: Record<string, string> = {};
    if (!data.providers.some((item) => String(item.providerId) === providerId)) local.provider = L("AI_ASSIGN_INVALID");
    if (!values.name.trim()) local.name = L("AI_MODEL_NAME_REQUIRED");
    if (!values.code.trim()) local.providerModelCode = L("AI_MODEL_CODE_REQUIRED");
    if (values.capabilities.length === 0) local.capabilities = L("AI_CAPABILITY_REQUIRED");
    const input = priceError(values.input);
    const cached = priceError(values.cached);
    const output = priceError(values.output);
    if (input) local.inputPrice = input;
    if (cached) local.cachedPrice = cached;
    if (output) local.outputPrice = output;
    return local;
  };
  const current = { name, code, capabilities, input: inputPrice, cached: cachedPrice, output: outputPrice };
  // E81: un error se va en cuanto el valor es válido; solo se muestran los que ya se marcaron al guardar.
  const refresh = (next: typeof current) => {
    if (Object.keys(errors).length === 0) return;
    const now = check(next);
    setErrors((previous) => Object.fromEntries(Object.entries(previous).filter(([field]) => now[field])));
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const local = check(current);
    if (Object.keys(local).length > 0) {
      showResult(() => (setErrors(local), setSummary(true), setGeneral(null)), true);
      return;
    }
    setBusy(true);
    setErrors({});
    setSummary(false);
    setGeneral(null);
    const reply = await postJson<{ saved?: boolean; name?: string; fields?: FieldError[] } & AiError>(
      "/api/admin/ai/manual",
      {
        providerId: Number(providerId),
        name,
        providerModelCode: code,
        protocol,
        capabilities,
        inputPrice: parse(inputPrice),
        cachedPrice: parse(cachedPrice),
        outputPrice: parse(outputPrice),
      },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(L("ADM_AI_MODEL_CREATED"));
      navigate("/Ai/Modelos/Index?scope=all");
      return;
    }
    const fields =
      reply.body?.fields ??
      (reply.body?.field ? [{ field: reply.body.field, messageKey: reply.body.messageKey ?? "" }] : []);
    if (fields.length > 0)
      showResult(
        () => (setErrors(Object.fromEntries(fields.map((item) => [item.field, L(item.messageKey)]))), setSummary(true)),
        true,
      );
    else showResult(() => setGeneral(errorText(L, reply.body)), false);
  };
  const toggle = (value: string) => {
    const next = capabilities.includes(value)
      ? capabilities.filter((item) => item !== value)
      : [...capabilities, value];
    setCapabilities(next);
    refresh({ ...current, capabilities: next });
  };
  const errorCount = Object.keys(errors).length;
  const available = capabilityOrder.filter((value) => data.capabilities.includes(value));

  return (
    <>
      <PageHead title={L("ADM_AI_MANUAL_TITLE")} sub={L("ADM_AI_MANUAL_SUB")} back={back}>
        <span className={styles.desktopOnly}>
          <a className="tm-btn secondary" href="/Ai/Modelos/Index">
            {L("ACTION_CANCEL")}
          </a>
          <button
            type="submit"
            form="manual-form"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {busy ? L("NPA_AI_SAVING") : L("ADM_AI_CREATE_MODEL")}
          </button>
        </span>
      </PageHead>
      <form
        id="manual-form"
        className={`tm-form ${styles.formGrid}`}
        noValidate
        onSubmit={(event) => void submit(event)}
      >
        {general ? (
          <div className={styles.formSummary}>
            <Notice tone="error">{general}</Notice>
          </div>
        ) : null}
        {summary && errorCount > 0 ? (
          <div className={styles.formSummary}>
            <Notice tone="error">
              {formatText(
                L(errorCount === 1 ? "ADM_AI_FORM_ERRORS_ONE" : "ADM_AI_FORM_ERRORS"),
                errorCount.toLocaleString(locale),
              )}
            </Notice>
          </div>
        ) : null}
        <Field id="manual-provider" label={L("ADM_AI_PROVIDER")} error={errors.provider} field="provider">
          <ChoiceMenu
            id="manual-provider"
            label={L("ADM_AI_PROVIDER")}
            value={providerId}
            options={data.providers.map((item) => ({ value: String(item.providerId), label: item.name }))}
            onChange={(value) => {
              setProviderId(value);
              if (errors.provider)
                setErrors((current) =>
                  Object.fromEntries(Object.entries(current).filter(([key]) => key !== "provider")),
                );
            }}
            invalid={!!errors.provider}
            searchLabel={L("ADM_AI_SEARCH_PROVIDER")}
            clearSearchLabel={L("ADM_AI_CLEAR_SEARCH")}
            searchIcon={<ChatIcon name="search" />}
            clearIcon={<ChatIcon name="x" />}
            chevron={<ChatIcon name="chevron-down" />}
            check={<ChatIcon name="check" />}
          />
        </Field>
        <Field id="manual-protocol" label={L("ADM_AI_PROTOCOL")} error={errors.protocol} field="protocol">
          <ChoiceMenu
            id="manual-protocol"
            label={L("ADM_AI_PROTOCOL")}
            value={protocol}
            options={data.protocols.map((value) => ({ value, label: protocolName(L, value) }))}
            onChange={setProtocol}
            chevron={<ChatIcon name="chevron-down" />}
            check={<ChatIcon name="check" />}
          />
        </Field>
        <TextField
          id="manual-name"
          field="name"
          label={L("ADM_AI_NAME")}
          value={name}
          maxLength={200}
          placeholder={L("ADM_AI_MANUAL_NAME_HINT")}
          error={errors.name}
          onChange={(value) => (setName(value), refresh({ ...current, name: value }))}
        />
        <TextField
          id="manual-code"
          field="providerModelCode"
          label={L("ADM_AI_MODEL_ID")}
          value={code}
          maxLength={200}
          placeholder={L("ADM_AI_MODEL_ID_HINT")}
          error={errors.providerModelCode}
          onChange={(value) => (setCode(value), refresh({ ...current, code: value }))}
        />
        <div className={`tm-field ${styles.full}${errors.capabilities ? " invalid" : ""}`} data-field="capabilities">
          <span id="manual-capabilities-label" className={styles.fieldLabel}>
            {L("ADM_AI_CAPABILITIES")}
          </span>
          <div role="group" aria-labelledby="manual-capabilities-label" className={styles.checks}>
            {available.map((value, index) => {
              const chosen = capabilities.includes(value);
              return (
                <button
                  key={value}
                  type="button"
                  role="checkbox"
                  className={styles.check}
                  aria-checked={chosen ? "true" : "false"}
                  aria-invalid={index === 0 && errors.capabilities ? "true" : undefined}
                  aria-describedby={errors.capabilities ? "manual-capabilities-error" : "manual-capabilities-help"}
                  onClick={() => toggle(value)}
                >
                  <span className={styles.box} aria-hidden="true">
                    {chosen ? <ChatIcon name="check" /> : null}
                  </span>
                  <span>{L(capabilityKeys[value] ?? "USAGE_UNKNOWN")}</span>
                </button>
              );
            })}
          </div>
          {errors.capabilities ? (
            <span id="manual-capabilities-error" className="tm-field-error">
              {errors.capabilities}
            </span>
          ) : null}
          <small id="manual-capabilities-help" className="tm-field-help">
            {L("ADM_AI_CAPABILITIES_HELP")}
          </small>
        </div>
        {/* C24 1.14.11 · los tres precios en una columna: entrada, entrada en caché (opcional) y salida. */}
        <div className={styles.priceFields} data-part="precios">
          <TextField
            id="manual-input-price"
            field="inputPrice"
            label={L("ADM_AI_INPUT_PRICE")}
            optional={L("CFG1_R1_OPTIONAL")}
            value={inputPrice}
            maxLength={20}
            placeholder={L("ADM_AI_PRICE_HINT")}
            error={errors.inputPrice}
            onChange={(value) => (setInputPrice(value), refresh({ ...current, input: value }))}
          />
          <TextField
            id="manual-cached-price"
            field="cachedPrice"
            label={L("ADM_AI_CACHED_PRICE")}
            optional={L("CFG1_R1_OPTIONAL")}
            // H11: «(opcional)» como los otros dos; la ayuda sin «Opcional.» (la de la ventana queda igual).
            help={L("ADM_AI_CACHED_PRICE_HELP_FORM")}
            value={cachedPrice}
            maxLength={20}
            placeholder={L("ADM_AI_PRICE_HINT")}
            error={errors.cachedPrice}
            onChange={(value) => (setCachedPrice(value), refresh({ ...current, cached: value }))}
          />
          <TextField
            id="manual-output-price"
            field="outputPrice"
            label={L("ADM_AI_OUTPUT_PRICE")}
            optional={L("CFG1_R1_OPTIONAL")}
            value={outputPrice}
            maxLength={20}
            placeholder={L("ADM_AI_PRICE_HINT")}
            error={errors.outputPrice}
            onChange={(value) => (setOutputPrice(value), refresh({ ...current, output: value }))}
          />
        </div>
        <div className={styles.mobileOnly}>
          <FormActions>
            <a className="tm-btn secondary" href="/Ai/Modelos/Index">
              {L("ACTION_CANCEL")}
            </a>
            <button type="submit" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
              {busy ? L("NPA_AI_SAVING") : L("ADM_AI_CREATE_MODEL")}
            </button>
          </FormActions>
        </div>
      </form>
    </>
  );
}
